import * as React from "react";
import { Link } from "react-router-dom";
import { IconChartBar, IconTable } from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import type { StatValue } from "@/types/dashboard";
import { StatGrid } from "./stat-grid";

/** How long the turn takes; matches the Tailwind duration classes below. */
const TURN_MS = 500;

/**
 * "rest": no 3D anywhere, one face in the layout, the other hidden.
 * "prep": one frame with the 3D context up and the card already turned, so a
 *         turn back can animate from 180° to 0°.
 * "turning": the rotation is in flight.
 */
type Stage = "rest" | "prep" | "turning";

/**
 * The one card chrome on the dashboard: title row, optional control, "Stats"
 * flip and "View all" link, then the body. With `stats` the body can turn
 * over to a four-up summary.
 *
 * The 3D flip exists only while it animates. At rest the card is a plain box:
 * no perspective, no preserve-3d, no transform, no backface rule, and the
 * face that is not showing is `visibility: hidden`. Anything else leaves a
 * 3D rendering context around the table, and the compositor's wheel hit test
 * in a visible tab can then route the wheel to the wrong face.
 */
export function DashboardCard({
  id,
  title,
  to,
  action,
  stats,
  fixed,
  fill = false,
  className,
  bodyClassName,
  children,
}: {
  id?: string;
  title: string;
  /** Route of the full page this card summarises. Renders a "View all" link. */
  to?: string;
  /** A control for the title row, such as the period toggle. */
  action?: React.ReactNode;
  /** Four figures shown when the card is flipped. */
  stats?: StatValue[];
  /** Content that stays in place above the part that flips. */
  fixed?: React.ReactNode;
  /** Stretch to the grid row's height and let the body scroll inside it. */
  fill?: boolean;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  const canFlip = !!stats && stats.length > 0;
  // Which face is (or is becoming) the shown one.
  const [flipped, setFlipped] = React.useState(false);
  const [stage, setStage] = React.useState<Stage>("rest");
  // Whether the rotating element carries the 180° transform right now.
  const [rotated, setRotated] = React.useState(false);
  const showsBack = canFlip && flipped;
  const inMotion = canFlip && stage !== "rest";

  const toggle = () => {
    if (!canFlip || stage !== "rest") return;
    if (!flipped) {
      // Front to back: bring the 3D context up and turn in one go.
      setStage("turning");
      setRotated(true);
      setFlipped(true);
    } else {
      // Back to front: first stand the card at 180° without a transition,
      // then turn it to 0° on the next frame.
      setStage("prep");
      setRotated(true);
    }
  };

  const turningRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (stage !== "prep") return;
    // Flush styles so the browser has registered the 180° start position,
    // then turn. A timeout rather than requestAnimationFrame: Chrome pauses
    // animation frames in a background tab, and the card would stay stuck
    // mid-flip until the tab came back.
    void turningRef.current?.offsetWidth;
    const timer = window.setTimeout(() => {
      setStage("turning");
      setRotated(false);
      setFlipped(false);
    }, 20);
    return () => window.clearTimeout(timer);
  }, [stage]);

  React.useEffect(() => {
    if (stage !== "turning") return;
    const timer = window.setTimeout(() => {
      setStage("rest");
      setRotated(false);
    }, TURN_MS + 20);
    return () => window.clearTimeout(timer);
  }, [stage]);

  // A flip must not shove the page around. The card keeps the table's height
  // and the stat grid stretches to fill it; only when the grid is taller than
  // a short table does the card grow, and then it glides. Both faces are
  // measured at their natural height for that. Filled cards keep a fixed
  // height and need none of this.
  const frontRef = React.useRef<HTMLDivElement>(null);
  const backRef = React.useRef<HTMLDivElement>(null);
  const showsBackRef = React.useRef(showsBack);
  showsBackRef.current = showsBack;
  const [faceHeights, setFaceHeights] = React.useState<{ front: number; back: number } | null>(null);
  const animateHeight = canFlip && !fill;
  React.useLayoutEffect(() => {
    if (!animateHeight) return;
    const measure = () =>
      setFaceHeights((prev) => ({
        front: frontRef.current?.offsetHeight ?? prev?.front ?? 0,
        // While the back shows it is stretched to the card, so its natural
        // height is only read while it rests behind the table.
        back: showsBackRef.current
          ? (prev?.back ?? 0)
          : (backRef.current?.offsetHeight ?? prev?.back ?? 0),
      }));
    measure();
    const observer = new ResizeObserver(measure);
    if (frontRef.current) observer.observe(frontRef.current);
    if (backRef.current) observer.observe(backRef.current);
    return () => observer.disconnect();
  }, [animateHeight]);
  const measured = animateHeight && faceHeights !== null;
  const bodyHeight = measured
    ? showsBack
      ? Math.max(faceHeights.front, faceHeights.back)
      : faceHeights.front
    : undefined;

  return (
    <section
      id={id}
      aria-label={title}
      className={cn(
        "flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card",
        fill && "lg:h-full",
        className
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3">
        <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
        <div className="flex items-center gap-2">
          {action}
          {canFlip && (
            <button
              type="button"
              aria-pressed={showsBack}
              onClick={toggle}
              className="inline-flex items-center gap-1 rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {showsBack ? (
                <IconTable size={14} stroke={1.75} />
              ) : (
                <IconChartBar size={14} stroke={1.75} />
              )}
              {showsBack ? "Table" : "Stats"}
            </button>
          )}
          {to && (
            <Link
              to={to}
              className="rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              View all
            </Link>
          )}
        </div>
      </header>

      {fixed}
      {/* The wrapper owns the animated height and the clipping; the element
          inside it is the one that turns, and it must keep overflow visible
          or the browser flattens its 3D transform. Perspective is only set
          while turning. */}
      <div
        className={cn(
          "min-w-0",
          inMotion && "[perspective:1400px]",
          fill && "flex min-h-0 flex-1 flex-col",
          measured && "relative overflow-hidden transition-[height] duration-500 ease-in-out motion-reduce:transition-none"
        )}
        style={bodyHeight !== undefined ? { height: bodyHeight } : undefined}
      >
        <div
          ref={turningRef}
          className={cn(
            "relative grid min-h-0",
            (fill || measured) && "h-full min-h-0 flex-1",
            inMotion && "[transform-style:preserve-3d]",
            rotated && "[transform:rotateY(180deg)]",
            stage === "turning"
              ? "transition-transform duration-500 ease-in-out motion-reduce:transition-none"
              : "transition-none"
          )}
        >
          {canFlip && (
            <div
              ref={backRef}
              className={cn(
                "min-h-0 min-w-0 overflow-hidden px-3 pb-3 [grid-area:1/1]",
                // Pre-turned by 180° only while the 3D context exists; at
                // rest it is a plain element.
                inMotion && "[backface-visibility:hidden] [transform:rotateY(180deg)]",
                !showsBack && "pointer-events-none",
                !showsBack && !inMotion && "invisible",
                // At rest behind the table: natural height. Showing:
                // stretched to the card so the grid fills it.
                animateHeight && (showsBack ? "absolute inset-0 flex flex-col" : "absolute inset-x-0 top-0")
              )}
              aria-hidden={!showsBack}
            >
              <StatGrid stats={stats!} stretch={animateHeight && showsBack} />
            </div>
          )}
          <div
            ref={frontRef}
            className={cn(
              "min-h-0 min-w-0 [grid-area:1/1]",
              fill && "flex flex-col",
              inMotion && "[backface-visibility:hidden]",
              showsBack && "pointer-events-none",
              showsBack && !inMotion && "invisible",
              // The table stays in flow and sets the height; while the back
              // shows it is parked at the top so the card's height can differ.
              animateHeight && showsBack && "absolute inset-x-0 top-0",
              bodyClassName
            )}
            aria-hidden={showsBack}
          >
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

const STATUS_TONE: Record<string, string> = {
  PENDING: "bg-amber-500",
  PAUSED: "bg-amber-500",
  APPROVED: "bg-emerald-500",
  ACTIVE: "bg-emerald-500",
  PAID: "bg-emerald-500",
  CHANGE_REQUESTED: "bg-amber-500",
  IN_USE: "bg-emerald-500",
  REJECTED: "bg-muted-foreground/60",
  INACTIVE: "bg-muted-foreground/60",
  SUSPENDED: "bg-red-500",
};

/** Status as a dot and a word, never a pill. `label` overrides the word
 *  when the raw status reads badly ("Awaiting review" for PENDING). */
export function StatusText({ status, label: given }: { status: string; label?: string }) {
  const label = given ?? status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-foreground">
      <span
        aria-hidden
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          STATUS_TONE[status] ?? "bg-muted-foreground/60"
        )}
      />
      {label}
    </span>
  );
}
